import { useLazyGetTemplateByIdQuery } from 'store/services/helios/templates';
import { memo, useCallback } from 'react';
import { styled, Typography as MuiTypography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { isMobileApp } from 'utils/common';
import { isValidId } from 'utils/object';

const Typography = styled(MuiTypography, {
  shouldForwardProp: (prop) => prop !== 'clickable',
})<{ clickable?: boolean }>(({ clickable }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  ...(clickable && {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  }),
}));

interface Props {
  id: number;
  name: string;
  detailTemplateId: string;
}

export const BrowseItem = memo(({ id, name, detailTemplateId }: Props) => {
  const { t } = useTranslation();
  const mobileApp = isMobileApp();
  const navigate = useNavigate();
  const clickable = isValidId(detailTemplateId);

  const [getTemplateById] = useLazyGetTemplateByIdQuery();

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (!clickable) return;
      try {
        const { data: template } = await getTemplateById({ id: detailTemplateId });
        if (!template?.moduleId) {
          throw new Error('Module ID not found in template');
        }

        const url = `/user/modules/${template.moduleId}/Helios/${detailTemplateId}/r/${id}/detail`;
        if (mobileApp) {
          navigate(url);
        } else {
          window.open(url, '_blank');
        }
      } catch (error) {
        toast.error(t('helios.listing.navigateError'));
      }
    },
    [clickable, detailTemplateId, getTemplateById, id, mobileApp, navigate, t]
  );

  return (
    <Typography clickable={clickable} onClick={clickable ? handleClick : undefined}>
      {name}
    </Typography>
  );
});
